import React, { useCallback, useEffect, useMemo, useState } from "react";
import { PushPin, X, Scales } from "@phosphor-icons/react";
import { API, http } from "@/lib/constants";
import { cn, formatLovelace, formatAdaBigCount, truncMid } from "@/lib/format";
import { CopyBtn, RowShareBtn } from "@/components/primitives";

const K = 500; // optimal pool count (k) — saturation reference

// ---------- top pools widget (home page) ----------
const useTopPools = (intervalMs = 60000, count = 12) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchRows = useCallback(async () => {
    try {
      const res = await http.get(`${API}/pools/extended`, { params: { count, order: "desc" } });
      setRows(Array.isArray(res.data) ? res.data : []);
      setError(null);
    } catch (e) {
      setError(e.response?.data?.detail || { message: e.message });
    } finally {
      setLoading(false);
    }
  }, [count]);

  useEffect(() => {
    fetchRows();
    const id = setInterval(fetchRows, intervalMs);
    return () => clearInterval(id);
  }, [fetchRows, intervalMs]);

  return { rows, loading, error };
};

const poolLabel = (r) => r.ticker || `${r.pool_id.slice(0, 10)}…`;
const marginPct = (m) => (m == null || m === "" ? null : Number(m) * 100);

// ---------- side-by-side compare panel ----------
const CompareMetric = ({ label, children }) => (
  <div className="grid grid-cols-[76px_1fr_1fr] gap-x-3 px-4 py-2 border-b border-primary/10 last:border-b-0 items-center">
    <span className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground mono">{label}</span>
    {children}
  </div>
);

const SatBar = ({ pct }) => {
  if (pct == null) return <span className="text-muted-foreground/60 text-[12px] mono">—</span>;
  const over = pct > 100;
  return (
    <div className="flex items-center gap-2 min-w-0">
      <div className="flex-1 h-2 rounded-full bg-primary/10 overflow-hidden">
        <div
          className="h-full rounded-full transition-all"
          style={{
            width: `${Math.min(100, Math.max(3, pct))}%`,
            background: over ? "#fb7185" : "hsl(190 95% 55%)",
            boxShadow: over ? "0 0 8px rgba(251,113,133,0.7)" : "0 0 8px rgba(34,211,238,0.6)",
          }}
        />
      </div>
      <span className={cn("text-[12px] mono tabnum shrink-0", over ? "text-rose-400" : "text-foreground/90")}>{pct.toFixed(1)}%</span>
    </div>
  );
};

const PoolCompare = ({ pinned, networkStake, amountMode, onUnpin, onClear, onOpen }) => {
  const sat = (liveStake) => {
    if (!networkStake) return null;
    const cap = Number(networkStake) / K;
    return cap > 0 ? (Number(liveStake) / cap) * 100 : null;
  };
  // Always render two value slots so columns stay aligned even with one pin.
  const slots = [pinned[0], pinned[1]];
  const cells = (fn) =>
    slots.map((p, idx) => (
      <div key={idx} className="text-[13px] mono tabnum text-foreground/90 min-w-0 truncate">
        {p ? fn(p) : <span className="text-muted-foreground/40">—</span>}
      </div>
    ));
  return (
    <div data-testid="pool-compare" className="border-b border-primary/15 bg-primary/[0.03]">
      <div className="px-4 py-2.5 flex items-center justify-between gap-3">
        <span className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-[#22d3ee] mono">
          <Scales size={14} weight="duotone" /> Compare
        </span>
        <button
          type="button"
          data-testid="pool-compare-clear"
          onClick={onClear}
          className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground hover:text-foreground mono"
        >
          Clear
        </button>
      </div>

      {/* pool headers */}
      <div className="grid grid-cols-[76px_1fr_1fr] gap-x-3 px-4 pb-2 items-end">
        <span className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground/70 mono">Pool</span>
        {slots.map((p, idx) =>
          p ? (
            <div key={idx} className="flex items-center gap-1.5 min-w-0">
              <button
                type="button"
                data-testid={`pool-compare-open-${idx}`}
                onClick={() => onOpen?.(p.pool_id)}
                className="truncate text-left text-[13px] mono text-[#22d3ee] hover:underline underline-offset-2"
                title="Open this pool"
              >
                {poolLabel(p)}
              </button>
              <button
                type="button"
                data-testid={`pool-compare-remove-${idx}`}
                onClick={() => onUnpin(p.pool_id)}
                aria-label="Remove from compare"
                className="inline-flex items-center justify-center h-5 w-5 rounded text-muted-foreground hover:text-rose-400 transition-colors shrink-0"
              >
                <X size={12} weight="bold" />
              </button>
            </div>
          ) : (
            <span key={idx} className="text-[11px] text-muted-foreground/40 mono">pin one more…</span>
          ),
        )}
      </div>

      <CompareMetric label="Live stake">{cells((p) => `${formatAdaBigCount(p.live_stake)} ₳`)}</CompareMetric>
      <CompareMetric label="Saturation">
        {slots.map((p, idx) => (p ? <SatBar key={idx} pct={sat(p.live_stake)} /> : <span key={idx} className="text-muted-foreground/40 text-[12px] mono">—</span>))}
      </CompareMetric>
      <CompareMetric label="Margin">{cells((p) => (marginPct(p.margin) == null ? "—" : `${marginPct(p.margin).toFixed(2)}%`))}</CompareMetric>
      <CompareMetric label="Fixed cost">{cells((p) => `${formatAdaBigCount(p.fixed_cost)} ₳`)}</CompareMetric>
      <CompareMetric label="Pledge">{cells((p) => `${formatAdaBigCount(p.pledge)} ₳`)}</CompareMetric>
      <CompareMetric label="Blocks (ep)">{cells((p) => p.blocks_this_epoch ?? "—")}</CompareMetric>
    </div>
  );
};

export const TopPools = ({ amountMode, onPickPool }) => {
  const { rows, loading, error } = useTopPools(60000, 12);
  const [pinned, setPinned] = useState([]); // up to 2 pool objects
  const [networkStake, setNetworkStake] = useState(null);

  useEffect(() => {
    let cancelled = false;
    http
      .get(`${API}/network/stats`)
      .then((res) => { if (!cancelled) setNetworkStake(res.data?.active_stake_total || null); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const pinnedIds = useMemo(() => new Set(pinned.map((p) => p.pool_id)), [pinned]);
  const togglePin = useCallback((pool) => {
    setPinned((prev) => {
      if (prev.some((p) => p.pool_id === pool.pool_id)) return prev.filter((p) => p.pool_id !== pool.pool_id);
      return [...prev, pool].slice(-2); // keep the two most recently pinned
    });
  }, []);
  const unpin = useCallback((id) => setPinned((prev) => prev.filter((p) => p.pool_id !== id)), []);

  return (
    <div data-testid="top-pools" className="relative overflow-hidden border border-primary/25 bg-card/30 backdrop-blur-md rounded-2xl box-glow">
      <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-primary to-transparent opacity-60 tracing-beam pointer-events-none" />
      <div className="px-5 py-3 border-b border-primary/15 flex items-center justify-between gap-3">
        <span className="text-[11px] uppercase tracking-[0.25em] text-[#22d3ee] mono">top stake pools</span>
        <span className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mono">
          pin up to 2 to compare
        </span>
      </div>

      {pinned.length > 0 ? (
        <PoolCompare
          pinned={pinned}
          networkStake={networkStake}
          amountMode={amountMode}
          onUnpin={unpin}
          onClear={() => setPinned([])}
          onOpen={onPickPool}
        />
      ) : null}

      {error && rows.length === 0 ? (
        <div className="px-5 py-6 text-[12px] text-muted-foreground mono">{error.message || "Couldn't load."}</div>
      ) : (
        <table className="w-full text-[13px] mono tabnum">
          <thead>
            <tr className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
              <th className="text-right font-sans font-medium px-3 py-2.5 border-b border-primary/10 w-16">Pin</th>
              <th className="text-right font-sans font-medium px-3 py-2.5 border-b border-primary/10 w-10">#</th>
              <th className="text-left font-sans font-medium px-3 py-2.5 border-b border-primary/10">Pool</th>
              <th className="text-right font-sans font-medium px-5 py-2.5 border-b border-primary/10">Live stake</th>
            </tr>
          </thead>
          <tbody>
            {(loading && rows.length === 0
              ? Array.from({ length: 6 }).map((_, i) => ({ skeleton: true, k: i }))
              : rows
            ).map((r, i) =>
              r.skeleton ? (
                <tr key={`pskel-${r.k}`} className="border-b border-primary/5">
                  {[0, 1, 2, 3].map((c) => (
                    <td key={c} className="px-3 py-2.5">
                      <span className="inline-block h-3 w-20 bg-primary/10 animate-pulse rounded-sm" />
                    </td>
                  ))}
                </tr>
              ) : (
                <tr
                  key={r.pool_id}
                  data-testid={`top-pool-row-${i}`}
                  className={cn(
                    "border-b border-primary/5 last:border-b-0 transition-colors",
                    pinnedIds.has(r.pool_id) ? "bg-[#22d3ee]/[0.06]" : "hover:bg-primary/5",
                  )}
                >
                  <td className="px-3 py-2.5 text-right">
                    <button
                      type="button"
                      data-testid={`top-pool-pin-${i}`}
                      onClick={() => togglePin(r)}
                      aria-label={pinnedIds.has(r.pool_id) ? "Unpin from compare" : "Pin to compare"}
                      title={pinnedIds.has(r.pool_id) ? "Unpin from compare" : "Pin to compare"}
                      className={cn(
                        "inline-flex items-center justify-center h-6 w-6 rounded-md border transition-colors",
                        pinnedIds.has(r.pool_id)
                          ? "border-[#22d3ee]/60 text-[#22d3ee] bg-[#22d3ee]/10"
                          : "border-primary/25 text-muted-foreground hover:border-[#22d3ee]/50 hover:text-[#22d3ee]",
                      )}
                    >
                      <PushPin size={13} weight={pinnedIds.has(r.pool_id) ? "fill" : "regular"} />
                    </button>
                  </td>
                  <td className="px-3 py-2.5 text-right text-muted-foreground">{i + 1}</td>
                  <td className="px-3 py-2.5">
                    <span className="flex items-center gap-1.5 min-w-0">
                      {r.ticker ? (
                        <button
                          type="button"
                          data-testid={`top-pool-link-${i}`}
                          onClick={() => onPickPool?.(r.pool_id)}
                          title="Open this pool"
                          className="shrink-0 inline-flex items-center px-2 py-0.5 border border-primary/30 bg-primary/5 text-[#22d3ee] text-[11px] tracking-[0.1em] rounded hover:border-[#22d3ee]/60 hover:bg-[#22d3ee]/10 transition-colors"
                        >
                          {r.ticker}
                        </button>
                      ) : null}
                      <button
                        type="button"
                        data-testid={r.ticker ? `top-pool-hash-${i}` : `top-pool-link-${i}`}
                        onClick={() => onPickPool?.(r.pool_id)}
                        title="Open this pool"
                        className="truncate min-w-0 text-left mono text-[13px] tabnum text-foreground/90 hover:text-[#22d3ee] transition-colors"
                      >
                        {truncMid(r.pool_id, r.ticker ? 8 : 12, 6)}
                      </button>
                      <span className="shrink-0 inline-flex items-center gap-1.5">
                        <CopyBtn value={r.pool_id} label={`pool-${i}`} />
                        <RowShareBtn modeKey="pools" id={r.pool_id} />
                      </span>
                    </span>
                  </td>
                  <td className="px-5 py-2.5 text-right text-foreground/90">{formatLovelace(r.live_stake, amountMode)}</td>
                </tr>
              ),
            )}
          </tbody>
        </table>
      )}
    </div>
  );
};
